import EntityDetailLayout from 'app/components/EntityDetailLayout';
import PostComposerModalFooter from 'app/modules/posts/modules/detail/components/PostComposerModalFooter';
import { PostModalOnClose } from 'app/modules/posts/modules/post/hooks/usePostModal';
import Header from 'components/posts/detail/composer/Header';
import Preview from 'components/posts/detail/composer/leftSection/Preview';
import Labels from 'components/posts/detail/composer/rightSection/Labels';
import Notes from 'components/posts/detail/composer/rightSection/Notes';
import PostComposerPlanning from 'app/modules/posts/modules/post/components/composer/PostComposerPlanning';
import React, { FC, useCallback } from 'react';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { popupConfirm } from 'utils/popup';
import PostComposerBoostBudget from 'app/modules/posts/modules/post/components/composer/PostComposerBoostBudget';
import PostComposerCommunication from 'app/modules/posts/modules/post/components/composer/PostComposerCommunication';
import { PostComposerContent } from 'app/modules/posts/modules/post/components/composer/PostComposerContent';
import { PostComposerLocalPosts } from 'app/modules/posts/modules/post/components/composer/PostComposerLocalPosts';
import { PageNumberType } from '@kontentino/kontentino-constants/Pages';
import { MediaUploadStatusProvider } from './composer/providers/MediaUploadStatusProvider';
import PostComposerPseudoTypeSwitch from 'app/modules/posts/modules/post/components/composer/PostComposerPseudoTypeSwitch';
import PostComposerPublishingMethod from 'app/modules/posts/modules/post/components/composer/PostComposerPublishingMethod';
import PostComposerTrialReel from 'app/modules/posts/modules/post/components/composer/PostComposerTrialReel';
import { useWidgetsSetupApi } from 'components/posts/detail/hooks/useWidgetsSetupApi';
import WidgetsSetup from './layout/widgetsSetup';
import PostBeforeScheduleAlert from 'app/components/postPreviewWithVariants/PostBeforeScheduleAlert';
import PostRequirements from 'components/posts/detail/overview/rightSection/infoCard/PostRequirements';
import { UserPermission } from 'constants/userPermission';
import UserPermissionGate from 'components/UserPermissionGate';
import { useTranslation } from 'react-i18next';

type Props = {
  onClose: PostModalOnClose;
  onSave: (options?: { confirmRepublish?: boolean }) => void;
  isSaving: boolean;
  postId: number | undefined;
  onPostStatusChange: (status: number) => void;
  postStatus: number;
  pageType: PageNumberType;
  wasPublished: boolean;
  pageName: string;
};

const Composer: FC<Props> = ({
  onClose,
  isSaving,
  pageType,
  onSave,
  postId,
  onPostStatusChange,
  postStatus,
  wasPublished,
  pageName,
}) => {
  const isEdit = !!postId;
  const needsEditConfirmation =
    postStatus === STATUS.POSTED || postStatus === STATUS.EXPORTED;
  // Saving onto SCHEDULED publishes the post, so an already-live post needs the republish
  // confirmation. EXPORTED is covered above, where the post is still live itself.
  const isRepublish = wasPublished && postStatus === STATUS.SCHEDULED;
  const { t } = useTranslation();

  const handleSave = useCallback(async () => {
    if (needsEditConfirmation) {
      const result = await popupConfirm({
        title: t('editPost', { defaultValue: 'Edit post' }),
        text: t('editPostedPostWarning', {
          defaultValue:
            "Editing this post will change status back to draft, any changes made won't be reflected in the version that was posted in your profile.",
        }),
        confirmButtonText: t('accept', { defaultValue: 'Accept' }),
        customClass: {
          popup: '!tw-w-[400px]',
        },
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    if (isRepublish) {
      const result = await popupConfirm({
        title: t('postAlreadyPublished', { pageName }),
        text: t('postAlreadyPublishedWarning', { pageName }),
        confirmButtonText: t('publishAgain'),
        customClass: {
          popup: '!tw-w-[400px]',
        },
      });

      if (!result.isConfirmed) {
        return;
      }

      onSave({ confirmRepublish: true });

      return;
    }

    onSave();
  }, [needsEditConfirmation, isRepublish, pageName, onSave, t]);

  const widgetsSetupApi = useWidgetsSetupApi({
    target: isEdit ? 'post-edit' : 'post-create',
  });

  return (
    <WidgetsSetup.Root
      widgetsSetup={widgetsSetupApi.query.data}
      defaultWidgetsSetup={widgetsSetupApi.DEFAULT_WIDGET_SETUP}
      onWidgetsSetupChange={(setup) => widgetsSetupApi.mutation.mutate(setup)}
      isLoading={widgetsSetupApi.query.isLoading}
    >
      <MediaUploadStatusProvider>
        <EntityDetailLayout
          header={<Header onClose={onClose} />}
          contentLeft={<Preview />}
          contentRight={
            <>
              {postId && <PostBeforeScheduleAlert id={postId} />}
              <PostComposerPseudoTypeSwitch />
              <PostComposerTrialReel />
              <WidgetsSetup.Layout>
                {{
                  postContent: ({ isExpanded }) => (
                    <PostComposerContent
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  publishingMethod: ({ isExpanded }) => (
                    <PostComposerPublishingMethod
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  postPlanning: ({ isExpanded }) => (
                    <PostComposerPlanning
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  boostBudget: ({ isExpanded }) => (
                    <PostComposerBoostBudget
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  labels: ({ isExpanded }) => (
                    <Labels accordionProps={{ initialIsOpen: isExpanded }} />
                  ),
                  notes: ({ isExpanded }) => (
                    <Notes accordionProps={{ initialIsOpen: isExpanded }} />
                  ),
                  communication: ({ isExpanded }) => (
                    <PostComposerCommunication
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  localPosts: ({ isExpanded }) => (
                    <PostComposerLocalPosts
                      accordionProps={{ initialIsOpen: isExpanded }}
                    />
                  ),
                  postRequirements: postId
                    ? ({ isExpanded }) => (
                        <UserPermissionGate
                          scopes={UserPermission.POST_SHOW_REQUIREMENTS}
                        >
                          <PostRequirements
                            id={postId}
                            accordionProps={{ initialIsOpen: isExpanded }}
                          />
                        </UserPermissionGate>
                      )
                    : undefined,
                }}
              </WidgetsSetup.Layout>
            </>
          }
          footer={
            <PostComposerModalFooter
              pageType={pageType}
              postStatus={postStatus}
              onCancelClick={onClose}
              onSaveClick={handleSave}
              onPostStatusChange={isEdit ? onPostStatusChange : undefined}
              confirmButtonLabel={isEdit ? t('saveChanges') : undefined}
            />
          }
          isLoading={isSaving}
        />
      </MediaUploadStatusProvider>
    </WidgetsSetup.Root>
  );
};

export default Composer;
